import { gql } from '@apollo/client';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip';
import { InfoCircleIcon, SwitchHorizontalIcon } from '@orn/shared-web/icons';

import { PATHS } from '../../../paths';
import { usePathWithDateRange } from '../../../providers/DateRangeProvider';
import Entity from '../../entity';
import RecordPane from '../../entity/RecordPane';
import { TRANSACTION_PAIR_TOOLTIP, TransactionPairValue } from '../rows';

import type {
  GeneralInfoPairedInvestmentTransactionFragment,
  GeneralInfoPairedTransactionFragment,
} from './__generated__/GeneralInfoSection.gql';

export const GeneralInfoPairedTransactionFragmentDocument = gql`
  fragment GeneralInfoPairedTransaction on Transaction {
    id
    amount
    currencyCode
    date
    financialAccount {
      id
      ...EntityAvatar
    }
  }
  fragment GeneralInfoPairedInvestmentTransaction on InvestmentTransaction {
    id
    amount
    currencyCode
    date
    financialAccount {
      id
      ...EntityAvatar
    }
  }
  ${Entity.Avatar.Fragment}
`;

export interface IGeneralInfoSectionProps {
  /** The other side of a transfer — `null` where the transaction has none. */
  pairedTransaction: GeneralInfoPairedTransactionFragment | null | undefined;
  /** The investment row on the other side of a transfer into or out of an investment account. */
  pairedInvestmentTransaction?: GeneralInfoPairedInvestmentTransactionFragment | null;
  className?: string;
}

/**
 * The "General Info" section of a transaction page (Figma "Transaction/Card/
 * General Info"): a Paired With row for each other side of a transfer, with an
 * info tooltip explaining the match. A bank pair links to its own page; an
 * investment pair has none, so it links to its account, keeping the date
 * range. Hidden when the transaction has no pair. The page query spreads
 * `GeneralInfoSection.Fragment`'s two fragments on the pairs and hands them down.
 */
const GeneralInfoSection = ({
  pairedTransaction,
  pairedInvestmentTransaction,
  className,
}: IGeneralInfoSectionProps) => {
  const addDateRangeToPath = usePathWithDateRange();

  if (!pairedTransaction && !pairedInvestmentTransaction) {
    return null;
  }

  const pairInfo = (
    <Tooltip title={TRANSACTION_PAIR_TOOLTIP}>
      <TooltipTrigger>
        <InfoCircleIcon className="size-3.5 shrink-0" />
      </TooltipTrigger>
    </Tooltip>
  );

  return (
    <RecordPane.Section title="General info" className={className}>
      {pairedTransaction && (
        <RecordPane.Row
          icon={<SwitchHorizontalIcon />}
          label="Paired with"
          labelAddon={pairInfo}
          to={PATHS.TRANSACTION(pairedTransaction.id)}
        >
          <TransactionPairValue pairedTransaction={pairedTransaction} />
        </RecordPane.Row>
      )}
      {pairedInvestmentTransaction && (
        <RecordPane.Row
          icon={<SwitchHorizontalIcon />}
          label="Paired with"
          labelAddon={pairInfo}
          to={addDateRangeToPath(PATHS.ACCOUNT(pairedInvestmentTransaction.financialAccount.id))}
        >
          <TransactionPairValue pairedTransaction={pairedInvestmentTransaction} />
        </RecordPane.Row>
      )}
    </RecordPane.Section>
  );
};

export default Object.assign(GeneralInfoSection, { Fragment: GeneralInfoPairedTransactionFragmentDocument });
